import React, { useState, useEffect, useCallback, useRef } from 'react';

import { InputAdornment, Fade, InputBase } from '@material-ui/core';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import Skeleton from '@material-ui/lab/Skeleton';
import NoDataComponent from 'components/NoDataComponent';
import InfiniteScroll from 'react-infinite-scroller';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { NewStyledIconButton, StyledDivider } from 'styles/common';

import { StyledIcon } from 'constants/icons';

import { search } from 'services/gif';
import { showErrorSnackbar } from 'services/snackbar';

const inputProps = {
  startAdornment: (
    <InputAdornment position="start">
      <StyledIcon color="textSecondary">
        <SearchRoundedIcon />
      </StyledIcon>
    </InputAdornment>
  ),
};

export default function Gif({ className = '', onSelect, toggleGifModal, isOpen }) {
  const [searchQuery, setSearchQuery] = useState('trending');
  const [results, setResults] = useState(null);
  const [next, setNext] = useState(undefined);
  const dispatch = useDispatch();
  const getResults = useCallback(
    (loadMore) => {
      search({ q: searchQuery, pos: next })
        .then((res) => {
          setResults(loadMore ? [...results, ...res.results] : [...res.results]);
          setNext(res.next);
        })
        .catch((err) => {
          dispatch(showErrorSnackbar('Sorry an error occured!'));
        });
    },
    [searchQuery, next, dispatch, results]
  );

  const resultsContainerRef = useRef(null);

  const handleWheel = useCallback((event) => {
    event.stopPropagation();

    if (!resultsContainerRef.current) return;

    resultsContainerRef.current.scrollTop += event.deltaY;
  }, []);

  useEffect(() => {
    const container = resultsContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [handleWheel]);

  useEffect(() => {
    getResults();
  }, []);

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      getResults();
    }
  }

  return (
    <Fade in={isOpen}>
      <Container className={className}>
        <Header>
          <BackContainer>
            <NewStyledIconButton onClick={toggleGifModal} color="textSecondary">
              <KeyboardBackspaceIcon />
            </NewStyledIconButton>
          </BackContainer>
          <SearchField
            placeholder="Search Tenor"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            // InputProps={inputProps}
            startAdornment={inputProps.startAdornment}
            // variant="outlined"
            margin="dense"
          />
        </Header>
        <StyledDivider />
        <ResultsContainer ref={resultsContainerRef}>
          {results === null ? (
            <NoDataComponent height="100%" message="Why so serious?" />
          ) : (
            <Results
              initialLoad={false}
              element="ul"
              pageStart={1}
              hasMore={next}
              loadMore={getResults.bind(null, true)}
              useWindow={false}
              getScrollParent={() => document.querySelector('.results-container')}
              loader={
                <div>
                  <Skeleton key="gifLoader" height="100px" width="25%" />
                  <Skeleton key="gifLoader" height="100px" width="25%" />
                </div>
              }
            >
              {results.map((result) => (
                <ListItem key={`gif${result.id}`}>
                  <Image src={result.previewUrl} onClick={onSelect && onSelect.bind(null, result.shareUrl)} />
                </ListItem>
              ))}
            </Results>
          )}
        </ResultsContainer>
      </Container>
    </Fade>
  );
}

const Container = styled.div`
  background: white;
  height: 50vh;
  width: 100%;
`;

const Header = styled.div`
  position: relative;
  height: 3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0rem 1rem;
`;

const BackContainer = styled.div`
  height: 3rem;
  display: flex;
  align-items: center;
`;

const ListItem = styled.li`
  height: 20vh;
  flex-grow: 1;
  padding: 2px;
`;

const ResultsContainer = styled.div`
  height: calc(100% - 4rem);
  overflow-y: auto;
`;

const Results = styled(InfiniteScroll)`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  // li:last-child {
  //   flex-grow: 10;
  //   height: 40vh;
  // }
  height: 100%;
  padding: 0;
`;

const Image = styled.img`
  max-height: 100%;
  min-width: 100%;
  object-fit: cover;
  vertical-align: bottom;
  &:hover {
    cursor: pointer;
  }
`;

const SearchField = styled(InputBase)`
  max-width: 50%;
  padding-top: 0.25rem;
  fieldset {
    border-radius: 50px;
  }
`;
